import { useEffect, useRef, useState } from 'react';
import { renderMapImage } from './renderMapImage';
import type { MapStyleKey } from './MapboxMap';

// Renders the current map (searched-location label baked in) to a PNG object
// URL for the preview surfaces — the Order sheet's wall preview and the AR
// model texture. Replaces the Static Images API URL those used to take,
// which can't draw the label or render the Suburbs style.
//
// One render per activation. The map image doesn't depend on print size, so
// changing size in the sheet/modal doesn't re-render it.

export type RenderedPreviewStatus = 'idle' | 'rendering' | 'ready' | 'error';

export interface RenderedMapPreviewParams {
  coords: [number, number];
  zoom: number;
  styleKey: MapStyleKey;
  label?: string | null;
  /** Current map centre's name (follows panning) — shown as the bottom
   *  badge on Streets. See renderMapImage.ts. */
  badgeLabel?: string | null;
  size?: number;
}

export function useRenderedMapPreview(
  active: boolean,
  params: RenderedMapPreviewParams | null,
  fallbackUrl?: string,
) {
  const [blobUrl, setBlobUrl] = useState('');
  const [status, setStatus] = useState<RenderedPreviewStatus>('idle');
  const blobUrlRef = useRef('');

  // Serialise the inputs so the render effect only re-fires on a real change,
  // not on every parent re-render handing us a fresh params object.
  const key =
    active && params
      ? [
          params.coords[0],
          params.coords[1],
          params.zoom,
          params.styleKey,
          params.label ?? '',
          params.badgeLabel ?? '',
          params.size ?? '',
        ].join('|')
      : '';

  useEffect(() => {
    if (!active || !params) {
      setStatus('idle');
      return;
    }

    const controller = new AbortController();
    let disposed = false;
    setStatus('rendering');

    renderMapImage({
      coords: params.coords,
      zoom: params.zoom,
      styleKey: params.styleKey,
      label: params.label,
      badgeLabel: params.badgeLabel,
      size: params.size,
      signal: controller.signal,
    })
      .then((blob) => {
        if (disposed) return;
        const url = URL.createObjectURL(blob);
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = url;
        setBlobUrl(url);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (disposed || (err as { name?: string })?.name === 'AbortError') return;
        console.error('Map preview render failed:', err);
        setStatus('error');
      });

    return () => {
      disposed = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Revoke the last blob URL on unmount.
  useEffect(
    () => () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    },
    [],
  );

  const imageUrl =
    status === 'ready'
      ? blobUrl
      : status === 'error'
        ? fallbackUrl ?? ''
        : '';

  return { imageUrl, status };
}
